/** @jsxImportSource theme-ui */
import React, { useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Box,
  Flex,
  Text,
  Button,
  TextInput,
  Spinner,
  PlaceholderMessage,
} from '@infotrack/zenith-ui';
import {
  useStaffById,
  useStaffList,
  useAddStaff,
  useUpdateStaff,
} from '../hooks';
import type { 
  ITopFiveApiV2EndpointsStaffAddStaffAddStaffRequest,
  ITopFiveApiV2EndpointsStaffUpdateStaffUpdateStaffRequest 
} from '@/api/models';
import {
  pageContainerStyles,
  headerStyles,
  formContainerStyles,
  formSectionStyles,
  sectionTitleStyles,
  formRowStyles,
  fieldStyles,
  labelStyles,
  checkboxGroupStyles,
  buttonGroupStyles,
} from './staffDetailsPage.styles';

// Local type definition for update staff request
type UpdateStaffApiRequest = ITopFiveApiV2EndpointsStaffUpdateStaffUpdateStaffRequest & {
  staffId: number;
};

const staffSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  surname: z.string().min(1, 'Surname is required'),
  email: z.string().email('Invalid email address'),
  username: z.string().min(1, 'Username is required'),
  employeeId: z.string().optional(),
  department: z.string().min(1, 'Department is required'),
  company: z.string().optional(),
  managerId: z.number({ required_error: 'Manager is required' }).min(1, 'Manager is required'),
  executiveId: z.number({ required_error: 'Executive is required' }).min(1, 'Executive is required'),
  cadence: z.enum(['Thirds', 'Half', 'Global']),
  isActive: z.boolean(),
  isManager: z.boolean(),
  isExecutive: z.boolean(),
});

type StaffFormValues = z.infer<typeof staffSchema>;

// Static options for cadence dropdown
const cadenceOptions = ['Thirds', 'Half', 'Global'];

export const StaffDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditMode = id !== 'new';
  const staffId = isEditMode ? parseInt(id!, 10) : 0;
  const [apiError, setApiError] = React.useState<string | null>(null);

  // Fetch staff data for edit mode
  const {
    staffDetail,
    isLoading: isLoadingStaff,
    isError: isStaffError,
  } = useStaffById(staffId, isEditMode);

  // Fetch staff list for managers/executives dropdown
  const {
    staffList,
    isLoading: isLoadingStaffList,
  } = useStaffList();

  // Mutation hooks for add/update
  const {
    addStaff,
    isPending: isAddPending,
    validationErrors: addValidationErrors,
    reset: resetAddMutation,
  } = useAddStaff();

  const {
    updateStaff,
    isPending: isUpdatePending,
    validationErrors: updateValidationErrors,
    reset: resetUpdateMutation,
  } = useUpdateStaff();

  // Derive managers, executives, and departments from staff list
  const managers = useMemo(() => {
    return staffList
      .filter(s => s.isManager)
      .map(s => ({ id: s.staffId ?? 0, name: s.fullName ?? '' }));
  }, [staffList]);

  const executives = useMemo(() => {
    return staffList
      .filter(s => s.isExecutive)
      .map(s => ({ id: s.staffId ?? 0, name: s.fullName ?? '' }));
  }, [staffList]);

  const departments = useMemo(() => {
    const deptSet = new Set<string>();
    staffList.forEach(s => {
      if (s.department) deptSet.add(s.department);
    });
    return Array.from(deptSet).sort();
  }, [staffList]);

  const companies = useMemo(() => {
    const companySet = new Set<string>();
    staffList.forEach(s => {
      if (s.company) companySet.add(s.company);
    });
    return Array.from(companySet).sort();
  }, [staffList]);

  // Combine validation errors from API
  const serverValidationErrors = isEditMode ? updateValidationErrors : addValidationErrors;

  const {
    control,
    handleSubmit,
    reset,
    setError: setFormError,
    formState: { errors, isSubmitting },
  } = useForm<StaffFormValues>({
    resolver: zodResolver(staffSchema),
    defaultValues: {
      firstName: '',
      surname: '',
      email: '',
      username: '',
      employeeId: '',
      department: '',
      company: '',
      managerId: 0,
      executiveId: 0,
      cadence: 'Thirds',
      isActive: true,
      isManager: false,
      isExecutive: false,
    },
  });

  // Reset form when staff detail is loaded
  useEffect(() => {
    if (isEditMode && staffDetail) {
      reset({
        firstName: staffDetail.firstName || '',
        surname: staffDetail.surname || '',
        email: staffDetail.staffEmail || '',
        username: staffDetail.username || '',
        employeeId: staffDetail.employeeId || '',
        department: staffDetail.department || '',
        company: staffDetail.company || '',
        managerId: staffDetail.managerId ?? 0,
        executiveId: staffDetail.executiveId ?? 0,
        cadence: (staffDetail.cadence as 'Thirds' | 'Half' | 'Global') || 'Thirds',
        isActive: staffDetail.isActive,
        isManager: staffDetail.isManager ?? false,
        isExecutive: staffDetail.isExecutive ?? false,
      });
    }
  }, [isEditMode, staffDetail, reset]);

  // Apply server validation errors to form
  useEffect(() => {
    if (Object.keys(serverValidationErrors).length > 0) {
      Object.entries(serverValidationErrors).forEach(([field, message]) => {
        setFormError(field as keyof StaffFormValues, {
          type: 'server',
          message,
        });
      });
    }
  }, [serverValidationErrors, setFormError]);

  const onSubmit = async (data: StaffFormValues) => {
    setApiError(null);
    resetAddMutation();
    resetUpdateMutation();

    try {
      if (isEditMode) {
        const updateRequest: UpdateStaffApiRequest = {
          staffId: staffId,
          firstName: data.firstName,
          surname: data.surname,
          employeeId: data.employeeId || '',
          email: data.email,
          department: data.department,
          company: data.company || '',
          managerId: data.managerId,
          executiveId: data.executiveId,
          isActive: data.isActive,
          isManager: data.isManager,
          isExecutive: data.isExecutive,
          username: data.username,
          cadence: data.cadence,
        };
        await updateStaff(updateRequest);
      } else {
        const addRequest: ITopFiveApiV2EndpointsStaffAddStaffAddStaffRequest = {
          firstName: data.firstName,
          surname: data.surname,
          employeeId: data.employeeId || '',
          email: data.email,
          department: data.department,
          managerId: data.managerId,
          executiveId: data.executiveId,
          isActive: data.isActive,
          isManager: data.isManager,
          isExecutive: data.isExecutive,
          username: data.username,
          cadence: data.cadence,
        };
        await addStaff(addRequest);
      }
      
      navigate('/staff');
    } catch (err) {
      // Validation errors are handled by the hooks and applied to form
      // Only set general error for unexpected failures
      const axiosError = err as { response?: { status?: number } };
      if (axiosError.response?.status === 403) {
        setApiError('You do not have permission to perform this action');
      } else if (axiosError.response?.status === 404) {
        setApiError('Staff member not found');
      } else if (!axiosError.response?.status || axiosError.response.status >= 500) {
        setApiError('An unexpected error occurred. Please try again later.');
      }
      // For 400 errors, validation errors are shown on the form fields
    }
  };

  const handleCancel = () => {
    navigate('/staff');
  };

  const isLoading = isLoadingStaff || isLoadingStaffList;
  const isSaving = isAddPending || isUpdatePending || isSubmitting;

  if (isLoading) {
    return (
      <Box sx={pageContainerStyles}>
        <Flex sx={{ justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <Spinner size="large" />
        </Flex>
      </Box>
    );
  }

  if (isStaffError && isEditMode) {
    return (
      <Box sx={pageContainerStyles}>
        <PlaceholderMessage title="Error" subtitle="Staff member not found or could not be loaded" />
        <Box sx={{ mt: 3 }}>
          <Button onClick={handleCancel} variant="secondary">
            Back to Staff List
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={pageContainerStyles}>
      <Flex sx={headerStyles}>
        <Text variant="hero">{isEditMode ? 'Edit Staff Member' : 'Add New Staff Member'}</Text>
        <Button
          variant="secondary"
          size="small"
          onClick={handleCancel}
          iconProps={{ name: 'arrow_back' }}
        >
          Back
        </Button>
      </Flex>

      <Box sx={formContainerStyles}>
        {apiError && (
          <Box
            sx={{
              mb: 3,
              p: 3,
              bg: '#fee2e2',
              border: '1px solid #fecaca',
              borderRadius: '0.375rem',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <span style={{ color: '#dc2626' }}>{apiError}</span>
            <Button
              variant="secondary"
              size="small"
              onClick={() => setApiError(null)}
            >
              Dismiss
            </Button>
          </Box>
        )}

        <form onSubmit={handleSubmit(onSubmit)}>
          {/* Personal Information */}
          <Box sx={formSectionStyles}>
            <Box sx={sectionTitleStyles}>
              <Text>Personal Information</Text>
            </Box>
            
            <Box sx={formRowStyles}>
              <Box sx={fieldStyles}>
                <Box sx={labelStyles}>
                  <Text>First Name <span style={{ color: 'red' }}>*</span></Text>
                </Box>
                <Controller
                  name="firstName"
                  control={control}
                  render={({ field }) => (
                    <>
                      <TextInput
                        {...field}
                        validationStatus={errors.firstName ? 'error' : undefined}
                        placeholder="Enter first name"
                      />
                      {errors.firstName && (
                        <Box sx={{ mt: 1, fontSize: '0.75rem', color: 'error/600' }}>
                          {errors.firstName.message}
                        </Box>
                      )}
                    </>
                  )}
                />
              </Box>

              <Box sx={fieldStyles}>
                <Box sx={labelStyles}>
                  <Text>Surname <span style={{ color: 'red' }}>*</span></Text>
                </Box>
                <Controller
                  name="surname"
                  control={control}
                  render={({ field }) => (
                    <>
                      <TextInput
                        {...field}
                        validationStatus={errors.surname ? 'error' : undefined}
                        placeholder="Enter surname"
                      />
                      {errors.surname && (
                        <Box sx={{ mt: 1, fontSize: '0.75rem', color: 'error/600' }}>
                          {errors.surname.message}
                        </Box>
                      )}
                    </>
                  )}
                />
              </Box>
            </Box>

            <Box sx={formRowStyles}>
              <Box sx={fieldStyles}>
                <Box sx={labelStyles}>
                  <Text>Email <span style={{ color: 'red' }}>*</span></Text>
                </Box>
                <Controller
                  name="email"
                  control={control}
                  render={({ field }) => (
                    <>
                      <TextInput
                        {...field}
                        type="email"
                        validationStatus={errors.email ? 'error' : undefined}
                        placeholder="Enter email address"
                      />
                      {errors.email && (
                        <Box sx={{ mt: 1, fontSize: '0.75rem', color: 'error/600' }}>
                          {errors.email.message}
                        </Box>
                      )}
                    </>
                  )}
                />
              </Box>

              <Box sx={fieldStyles}>
                <Box sx={labelStyles}>
                  <Text>Username <span style={{ color: 'red' }}>*</span></Text>
                </Box>
                <Controller
                  name="username"
                  control={control}
                  render={({ field }) => (
                    <>
                      <TextInput
                        {...field}
                        validationStatus={errors.username ? 'error' : undefined}
                        placeholder="Enter username"
                      />
                      {errors.username && (
                        <Box sx={{ mt: 1, fontSize: '0.75rem', color: 'error/600' }}>
                          {errors.username.message}
                        </Box>
                      )}
                    </>
                  )}
                />
              </Box>
            </Box>

            <Box sx={formRowStyles}>
              <Box sx={fieldStyles}>
                <Box sx={labelStyles}>
                  <Text>Employee ID</Text>
                </Box>
                <Controller
                  name="employeeId"
                  control={control}
                  render={({ field }) => (
                    <TextInput
                      {...field}
                      placeholder="Enter employee ID"
                    />
                  )}
                />
              </Box>
            </Box>
          </Box>

          {/* Work Information */}
          <Box sx={formSectionStyles}>
            <Box sx={sectionTitleStyles}>
              <Text>Work Information</Text>
            </Box>
            
            <Box sx={formRowStyles}>
              <Box sx={fieldStyles}>
                <Box sx={labelStyles}>
                  <Text>Department <span style={{ color: 'red' }}>*</span></Text>
                </Box>
                <Controller
                  name="department"
                  control={control}
                  render={({ field }) => (
                    <>
                      <select
                        {...field}
                        style={{
                          width: '100%',
                          padding: '0.5rem',
                          borderRadius: '0.25rem',
                          border: errors.department ? '1px solid #e74c3c' : '1px solid #ccc',
                          fontSize: '0.875rem',
                        }}
                      >
                        <option value="" disabled hidden>Select department</option>
                        {departments.map((dept) => (
                          <option key={dept} value={dept}>
                            {dept}
                          </option>
                        ))}
                      </select>
                      {errors.department && (
                        <Box sx={{ mt: 1, fontSize: '0.75rem', color: 'error/600' }}>
                          {errors.department.message}
                        </Box>
                      )}
                    </>
                  )}
                />
              </Box>

              <Box sx={fieldStyles}>
                <Box sx={labelStyles}>
                  <Text>Cadence <span style={{ color: 'red' }}>*</span></Text>
                </Box>
                <Controller
                  name="cadence"
                  control={control}
                  render={({ field }) => (
                    <>
                      <select
                        {...field}
                        style={{
                          width: '100%',
                          padding: '0.5rem',
                          borderRadius: '0.25rem',
                          border: errors.cadence ? '1px solid #e74c3c' : '1px solid #ccc',
                          fontSize: '0.875rem',
                        }}
                      >
                        {cadenceOptions.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                      {errors.cadence && (
                        <Box sx={{ mt: 1, fontSize: '0.75rem', color: 'error/600' }}>
                          {errors.cadence.message}
                        </Box>
                      )}
                    </>
                  )}
                />
              </Box>
            </Box>

            <Box sx={formRowStyles}>
              <Box sx={fieldStyles}>
                <Box sx={labelStyles}>
                  <Text>Manager <span style={{ color: 'red' }}>*</span></Text>
                </Box>
                <Controller
                  name="managerId"
                  control={control}
                  render={({ field }) => (
                    <>
                      <select
                        value={field.value && field.value > 0 ? field.value.toString() : ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          field.onChange(value ? parseInt(value, 10) : undefined);
                        }}
                        onBlur={field.onBlur}
                        style={{
                          width: '100%',
                          padding: '0.5rem',
                          borderRadius: '0.25rem',
                          border: errors.managerId ? '1px solid #e74c3c' : '1px solid #ccc',
                          fontSize: '0.875rem',
                        }}
                      >
                        <option value="" disabled hidden>Select manager</option>
                        {managers.map((manager) => (
                          <option key={manager.id} value={manager.id}>
                            {manager.name}
                          </option>
                        ))}
                      </select>
                      {errors.managerId && (
                        <Box sx={{ mt: 1, fontSize: '0.75rem', color: 'error/600' }}>
                          {errors.managerId.message}
                        </Box>
                      )}
                    </>
                  )}
                />
              </Box>

              <Box sx={fieldStyles}>
                <Box sx={labelStyles}>
                  <Text>Executive <span style={{ color: 'red' }}>*</span></Text>
                </Box>
                <Controller
                  name="executiveId"
                  control={control}
                  render={({ field }) => (
                    <>
                      <select
                        value={field.value && field.value > 0 ? field.value.toString() : ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          field.onChange(value ? parseInt(value, 10) : undefined);
                        }}
                        onBlur={field.onBlur}
                        style={{
                          width: '100%',
                          padding: '0.5rem',
                          borderRadius: '0.25rem',
                          border: errors.executiveId ? '1px solid #e74c3c' : '1px solid #ccc',
                          fontSize: '0.875rem',
                        }}
                      >
                        <option value="" disabled hidden>Select executive</option>
                        {executives.map((executive) => (
                          <option key={executive.id} value={executive.id}>
                            {executive.name}
                          </option>
                        ))}
                      </select>
                      {errors.executiveId && (
                        <Box sx={{ mt: 1, fontSize: '0.75rem', color: 'error/600' }}>
                          {errors.executiveId.message}
                        </Box>
                      )}
                    </>
                  )}
                />
              </Box>
            </Box>
          </Box>

          {/* Status & Roles */}
          <Box sx={formSectionStyles}>
            <Box sx={sectionTitleStyles}>
              <Text>Status & Roles</Text>
            </Box>
            
            <Box sx={checkboxGroupStyles}>
              <Controller
                name="isActive"
                control={control}
                render={({ field }) => (
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                      style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                    />
                    <Text>Active</Text>
                  </label>
                )}
              />

              <Controller
                name="isManager"
                control={control}
                render={({ field }) => (
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                      style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                    />
                    <Text>Manager</Text>
                  </label>
                )}
              />

              <Controller
                name="isExecutive"
                control={control}
                render={({ field }) => (
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                      style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                    />
                    <Text>Executive</Text>
                  </label>
                )}
              />
            </Box>
          </Box>

          {/* Action Buttons */}
          <Flex sx={buttonGroupStyles}>
            <Button
              type="button"
              variant="secondary"
              onClick={handleCancel}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={isSaving}
            >
              {isSaving ? 'Saving...' : isEditMode ? 'Update Staff' : 'Add Staff'}
            </Button>
          </Flex>
        </form>
      </Box>
    </Box>
  );
};
